import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { RentResponse } from '../../../api/client/data-contracts';
import Table from 'antd/lib/table';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, FormInstance, Input, InputRef, Popconfirm } from 'antd';
import AddRentEquipmentModal from './rent-lines/add-rent-equipment-modal';
import { useRents } from './useRents';
import { useParams } from 'react-router-dom';
import { getUserData } from '../../../api/auth';
import { showErrorNotification } from '../assets/useAssets';

export interface Props {
  rent: RentResponse;
}

export interface PriceProps {
  value: number;
  currency?: string;
}

export const Price: FC<PriceProps> = ({ value, currency = '€' }) => {
  return (
    <p className='hp-mb-0 hp-text-color-primary-1 hp-font-weight-500'>
      {value} {currency}
    </p>
  );
};

export const SerialNumber: FC<{ children: number | string }> = ({
  children,
}) => {
  return (
    <p className='hp-mb-0 hp-text-color-primary-1 hp-font-weight-500'>
      {children}
    </p>
  );
};
interface DataType {
  key: string;
  asset: string;
  serialNumber: string | number;
  lineOrder: number;
  paymentValue: React.ReactNode;
  paymentValueWithVat: React.ReactNode;
  otherValues: React.ReactNode;
  value: React.ReactNode;
}

/*Docs 
Please follow the docs at to know how this works https://ant.design/components/table#components-table-demo-edit-cell
*/
const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface RentLine extends DataType {}

interface EditableRowProps {
  index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext)!;
  const intl = useIntl();
  useEffect(() => {
    if (editing) {
      inputRef.current!.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();

      toggleEdit();
      handleSave({ ...record, ...values }, dataIndex);
    } catch (errInfo) {
      // Field validation errors are already surfaced inline by the Form.Item
      // rules; only report genuinely unexpected failures instead of swallowing
      // them silently.
      const isValidationError =
        !!errInfo && typeof errInfo === 'object' && 'errorFields' in errInfo;
      if (!isValidationError) {
        showErrorNotification('unexpected-error-message', intl);
      }
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className='editable-cell-value-wrap'
        style={{ paddingRight: 24 }}
        role='button'
        tabIndex={0}
        aria-label={intl.formatMessage({ id: 'edit' })}
        onClick={toggleEdit}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleEdit();
          }
        }}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

type EditableTableProps = Parameters<typeof Table>[0];

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof RentLine;
  record: RentLine;
  handleSave: (
    record: RentLine & { paymentValue: number; paymentValueWithVat: number },
    dataIndex: keyof RentLine,
  ) => void;
}

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>;

export const RentLines: FC<Props> = ({ rent }) => {
  const userData = getUserData();
  const userCanUpdate =
    (userData?.permissions || []).includes('rent:update') || userData?.isAdmin;

  const data: DataType[] = rent.rentLines.map((line) => ({
    key: line.id,
    lineOrder: line.lineOrder + 1,
    asset: line.asset.serialNumber.toString(),
    paymentValue: line.paymentValue.toFixed(2),
    paymentValueWithVat: line.paymentValueWithVat.toFixed(2),
    otherValues: (line.otherValues ?? 0).toFixed(2),
    value: (line.value ?? 0).toFixed(2),
    serialNumber: line.asset.serialNumber,
  }));

  function handleSave(
    rentLine: RentLine & { paymentValue: number; paymentValueWithVat: number },
    dataIndex: keyof RentLine,
  ) {
    let paymentValue = rentLine.paymentValue;

    if (dataIndex === 'paymentValueWithVat') {
      // We calculate the paymentValue
      paymentValue = (100 / 123) * rentLine.paymentValueWithVat;
    }
    updateRentEquipmentPaymentValue({
      rentId: id,
      dto: {
        asset: String(rentLine.serialNumber),
        paymentValue: Number(paymentValue),
        otherValues: Number(rentLine.otherValues),
        value: Number(rentLine.value),
      },
    });
  }

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: <FormattedMessage id='number' />,
      dataIndex: 'lineOrder',
      key: 'lineOrder',
    },
    {
      title: <FormattedMessage id='equipment-code' />,
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      render: (value) => <SerialNumber>{value}</SerialNumber>,
    },

    {
      title: 'Valor s/ IVA',
      dataIndex: 'paymentValue',
      key: 'paymentvalue',
      editable: true,
      render: (v) => <Price value={v} />,
    },
    {
      title: 'Valor c/ IVA',
      dataIndex: 'paymentValueWithVat',
      key: 'paymentValueWithVat',
      editable: true,
      render: (v) => <Price value={v} />,
    },
    {
      title: <FormattedMessage id='other-values' />,
      dataIndex: 'otherValues',
      key: 'otherValues',
      editable: true,
      render: (v) => <Price value={v} />,
    },
    {
      title: <FormattedMessage id='rent-line-value' />,
      dataIndex: 'value',
      key: 'value',
      editable: true,
      render: (v) => <Price value={v} />,
    },
    {
      title: <FormattedMessage id='actions' />,
      dataIndex: 'actions',
      render: (_: unknown, record: unknown) => {
        return rent.state === 'ORDERING' && userCanUpdate ? (
          <Popconfirm
            title={'Tem certeza que pretende remover este equipamento?'}
            onConfirm={() => handleDelete((record as DataType).serialNumber)}
          >
            <a>
              <FormattedMessage id='Remover'></FormattedMessage>
            </a>
          </Popconfirm>
        ) : null;
      },
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        editable: userCanUpdate,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [addingEquipment, setAddingEquipment] = useState(false);
  function toggleAddEquipment() {
    setAddingEquipment(!addingEquipment);
  }

  const {
    removeEquipment,
    rentsQuery: { isFetching },
    updateRentEquipmentPaymentValue,
  } = useRents();

  const { id } = useParams<{ id: string }>();

  function handleDelete(assetId: string | number) {
    removeEquipment({ rentId: id, dto: { asset: String(assetId) } });
  }

  return (
    <>
      <Table
        components={components}
        columns={columns as ColumnTypes}
        dataSource={data}
        loading={isFetching}
        rowClassName={() => 'editable-row'}
        footer={() =>
          rent.state === 'ORDERING' ? (
            <Button type='primary' onClick={toggleAddEquipment}>
              Adicionar equipamento
            </Button>
          ) : null
        }
      />
      <AddRentEquipmentModal
        open={addingEquipment}
        handleCancel={toggleAddEquipment}
      />
    </>
  );
};
